import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';

import { queryClient, queryKeys, sessionToken } from '@/shared/api';
import type { ApiError } from '@/shared/api';
import type { UUID } from '@/shared/types/api';

import type {
  LoginResponse,
  MiniAppLinkPayload,
  MiniAppLoginPayload,
  RequestOtpPayload,
  RequestOtpResponse,
  UpdateProfilePayload,
  User,
  VerifyOtpPayload,
} from '../types/auth.types';
import { authApi } from './auth.api';

/**
 * Adopt a freshly issued token and drop every cached response that belonged to
 * the previous session. Shared by all four login paths so no route can log a
 * user in while leaving another user's data in the cache.
 */
export function adoptSession(response: LoginResponse): void {
  sessionToken.set(response.access_token);
  queryClient.clear();
  queryClient.setQueryData(queryKeys.session.me(), response.user);
}

/* --------------------------------- Queries -------------------------------- */

export function useCurrentUser(
  options?: Partial<UseQueryOptions<User, ApiError, User, readonly unknown[]>>,
) {
  return useQuery<User, ApiError, User, readonly unknown[]>({
    queryKey: queryKeys.session.me(),
    queryFn: authApi.getMe,
    // The profile is the app's identity anchor; it must not be refetched
    // constantly, but it must be correct after any explicit invalidation.
    staleTime: 5 * 60_000,
    ...options,
  });
}

/** Another user's public profile — used to name members and payers. */
export function useUser(userId: UUID | undefined) {
  return useQuery<User, ApiError>({
    queryKey: queryKeys.users.detail(userId ?? ''),
    queryFn: () => authApi.getUser(userId!),
    enabled: Boolean(userId),
    // Names change rarely; a long window removes most of the fan-out cost of
    // resolving many members at once.
    staleTime: 10 * 60_000,
    // A manager may legitimately lack `user.view` over a given person; that is
    // a 403, not a transient failure, so it must not be retried.
    retry: false,
  });
}

/* -------------------------------- Mutations ------------------------------- */

export function useRequestOtp() {
  return useMutation<RequestOtpResponse, ApiError, RequestOtpPayload>({
    mutationFn: authApi.requestOtp,
  });
}

export function useVerifyOtp() {
  return useMutation<LoginResponse, ApiError, VerifyOtpPayload>({
    mutationFn: authApi.verifyOtp,
    onSuccess: adoptSession,
  });
}

export function useMiniAppLogin() {
  return useMutation<LoginResponse, ApiError, MiniAppLoginPayload>({
    mutationFn: authApi.miniAppLogin,
    onSuccess: adoptSession,
  });
}

export function useMiniAppLink() {
  return useMutation<LoginResponse, ApiError, MiniAppLinkPayload>({
    mutationFn: authApi.miniAppLink,
    onSuccess: adoptSession,
  });
}

export function useUpdateProfile() {
  const client = useQueryClient();
  return useMutation<User, ApiError, UpdateProfilePayload>({
    mutationFn: authApi.updateMe,
    onSuccess: (user) => {
      // The response is authoritative — seed it rather than refetching.
      client.setQueryData(queryKeys.session.me(), user);
    },
  });
}
